import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil, Trash2, Check, X, Eye, EyeOff } from 'lucide-react';

import { Modal } from '../../../components/ui/Modal.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { MarkdownEditor } from '../../../components/ui/MarkdownEditor.jsx';
import { Markdown } from '../../../components/ai/Markdown.jsx';
import { ragApi } from '../../../api/rag.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { fmt } from '../../../lib/formatters.js';

export function ChunksDrawer({ open, onClose, document }) {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('rag:upload');
  const canDelete = hasPermission('rag:delete');
  const docId = document && document._id;

  const [editing, setEditing] = useState(null);      // { chunkId, value }
  const [deleteTarget, setDeleteTarget] = useState(null); // chunk pending deletion

  const query = useQuery({
    queryKey: ['rag', 'chunks', docId],
    enabled: open && Boolean(docId),
    queryFn: () => ragApi.listChunks(docId, { limit: 50 })
  });
  const items = (query.data && query.data.items) || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rag', 'chunks', docId] });
    queryClient.invalidateQueries({ queryKey: ['rag', 'documents'] });
    queryClient.invalidateQueries({ queryKey: ['rag', 'stats'] });
  };

  const updateMutation = useMutation({
    mutationFn: ({ chunkId, body }) => ragApi.updateChunk(docId, chunkId, body),
    onSuccess: (_d, vars) => {
      if (vars.body.text !== undefined) setEditing(null);
      invalidate();
      toast.success('Chunk aggiornato.');
    },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  const deleteMutation = useMutation({
    mutationFn: (chunkId) => ragApi.deleteChunk(docId, chunkId),
    onSuccess: () => { setDeleteTarget(null); invalidate(); toast.success('Chunk eliminato.'); },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  const savingTextFor = updateMutation.isPending && updateMutation.variables && updateMutation.variables.body.text !== undefined
    ? updateMutation.variables.chunkId : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={document ? `Chunk di "${document.title}"` : 'Chunk'}
      description={document ? `${document.chunkCount ?? items.length} chunk indicizzati.` : null}
      size="xl"
    >
      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-muted">Nessun chunk indicizzato per questo documento.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {items.map((c, idx) => {
            const isEditing = editing && editing.chunkId === c._id;
            return (
              <li key={c._id} className="rounded-md border border-border bg-surface p-3 text-sm">
                <div className="mb-1 flex items-center justify-between gap-2 text-xs text-ink-muted">
                  <span>Chunk #{c.position ?? idx + 1}</span>
                  <span className="flex items-center gap-2">
                    {c.active ? <Badge tone="success">attivo</Badge> : <Badge>disattivato</Badge>}
                    {c.tokensEstimate ? <span>{fmt.int(c.tokensEstimate)} token</span> : null}
                    {(canEdit || canDelete) && !isEditing && (
                      <span className="flex items-center gap-0.5">
                        {canEdit && (
                          <>
                            <button
                              type="button"
                              aria-label={c.active ? 'Disattiva chunk' : 'Attiva chunk'}
                              title={c.active ? 'Disattiva (escluso dalla ricerca)' : 'Attiva'}
                              onClick={() => updateMutation.mutate({ chunkId: c._id, body: { active: !c.active } })}
                              className="rounded p-1 text-ink-faint hover:bg-surface-subtle hover:text-ink"
                            >
                              {c.active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              type="button"
                              aria-label="Modifica chunk"
                              onClick={() => setEditing({ chunkId: c._id, value: c.text || '' })}
                              className="rounded p-1 text-ink-faint hover:bg-surface-subtle hover:text-ink"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            aria-label="Elimina chunk"
                            onClick={() => setDeleteTarget(c)}
                            className="rounded p-1 text-ink-faint hover:bg-danger/10 hover:text-danger"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    )}
                  </span>
                </div>

                {isEditing ? (
                  <div className="flex flex-col gap-2">
                    <MarkdownEditor
                      minHeight={300}
                      value={editing.value}
                      onChange={(v) => setEditing({ chunkId: c._id, value: v })}
                    />
                    <p className="text-[11px] text-ink-faint">Salvando, il chunk viene ricalcolato (nuovo embedding).</p>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={savingTextFor === c._id}>
                        <X className="h-3.5 w-3.5" /> Annulla
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateMutation.mutate({ chunkId: c._id, body: { text: editing.value } })}
                        loading={savingTextFor === c._id}
                        disabled={!editing.value.trim()}
                      >
                        <Check className="h-3.5 w-3.5" /> Salva
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={c.active ? 'text-ink' : 'text-ink-faint opacity-70'}>
                    <Markdown>{c.text}</Markdown>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        destructive
        title="Elimina chunk"
        description={deleteTarget ? `Eliminare il chunk #${deleteTarget.position}? Verrà rimosso dall'indice e non sarà più usato nelle risposte.` : ''}
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget._id)}
      />
    </Modal>
  );
}
